import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { create } from 'lodash'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import useCustomMutation from '@/composables/useCustomMutation'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import type { Markup } from '@/interfaces/markup'
import { MutationParams } from '@/interfaces/mutationParams'

interface Props {
	params: Params
	immediate?: boolean
}

interface Params extends Omit<Partial<Markup>, 'id'> {
	id: string | number
	companyId: number | string
}

interface CreateParams {
	value: number
	type: string
	agencyType?: string | null
	agencyValue?: number | null
	companyId?: number
	serviceId: number
}

interface UpdateParams extends Params {}

const getCompanyMarkup = async (params: Params): Promise<Markup[]> => {
	const { data } = await axios.get<Markup[]>(
		`/api/companies/${params.companyId}/markups`,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)
	return data
}

export const createMarkup = async (params: CreateParams) => {
	const { data } = await axios.post(
		`/api/companies/${params.companyId}/markups`,
		params
	)
	return data
}

const updateMarkup = async (params: UpdateParams) => {
	const { data } = await axios.put(`/api/markups/${params.id}`, params)
	return data
}

const useCompanyMarkup = (props?: Props) => {
	const { params, immediate = true } = props || {}
	const toast = useToast()
	const companyId = ref(params?.companyId)

	const { data, isLoading, isError, refetch } = useQuery(
		['companyMarkup', params?.companyId, params?.id],
		() =>
			getCompanyMarkup({
				companyId: params?.id ?? (companyId.value as string | number),
				id: '',
			}),
		{
			enabled: params ? immediate : false,
			retry: false,
		}
	)

	const createMutation = useMutation(
		(mutationParams: MutationParams<CreateParams>) =>
			createMarkup(mutationParams.params),
		useCustomMutation(toast, 'create')
	)

	const updateMutation = useMutation(
		(mutationParams: MutationParams<UpdateParams>) =>
			updateMarkup(mutationParams.params),
		useCustomMutation(toast, 'update')
	)

	return {
		companyId,
		data,
		isError,
		isLoading,

		refetch,

		createMutation,
		createCompanyMarkupMutation: createMutation,
		createCompanyMarkup: createMutation.mutate,
		createCompanyMarkupAsync: createMutation.mutateAsync,
		createCompanyMarkupLoading: createMutation.isLoading,

		updateMutation,
		updateCompanyMarkupMutation: updateMutation,
		updateCompanyMarkup: updateMutation.mutate,
		updateCompanyMarkupAsync: updateMutation.mutateAsync,
		updateCompanyMarkupLoading: updateMutation.isLoading,
	}
}

export default useCompanyMarkup
